import React, { useEffect,useState } from 'react';
import { toast } from 'react-toastify';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultList,getSingleTemplateDetail,searchInstagramProfile } from "../../../../utils/instagramFeedApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";

const CommonTemplateWidget = ({ selectedTemplate,selectedLayout,isTemplateId,widgetId,instagramFeedTabData }) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);

    const {
        is_website_frontend,
        source_search_name,
        source_type,
        header_is_show_header,
        header_profile_picture,
        header_full_name,
        header_username,
        header_verified_badge,
        header_posts_count,
        header_followers_count,
        header_following_count,
        header_follow_button,
        feed_title,
        header_background_color,
        header_text_color,
        header_follow_button_color,
        header_follow_button_text_color,
        load_more_button,
        layout_custom_width,
        layout_is_full_width,
        layout_rows,
        layout_columns
    } = instagramFeedTabData || {};

    // Call Css
    useEffect(() => {
        // Remove other module CSS
        const oldLinks = document.querySelectorAll('link[data-dynamic-css]');
        oldLinks.forEach(link => link.remove());

        // Load CSS
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = `${process.env.PUBLIC_URL}/instagram-feed/temp-css/InstagramFeed.css`;
        if (widgetId) {
            link.setAttribute("instagram-feed-data-dynamic-css", "instagram-feed");
        } else {
            link.setAttribute("data-dynamic-css", "instagram-feed");
        }
        document.head.appendChild(link);

        // Clean up on unmount
        return () => {
            const thisLink = document.querySelector('link[data-dynamic-css="instagram-feed"]');
            if (thisLink) thisLink.remove();
        };
    }, [widgetId]);

    // Helper to get slidesToShow from actual window width
    const getSlidesToShow = (width, layout_columns, selectedTemplate) => {
        if (selectedTemplate === 'Small_Widget_Temp') {
            // For small widget template: limit max 2 items
            if (width < 768) return 1;
            return Math.min(layout_columns, 2); // max 2
        }

        // For all other templates
        if (width < 480) return 1;
        if (width < 768) return Math.min(layout_columns, 2);
        if (width < 1024) return Math.min(layout_columns, 3);

        return layout_columns;
    };

    // Use window.innerWidth instead of custom layout width
    const [slidesToShow, setSlidesToShow] = useState(
    getSlidesToShow(window.innerWidth, layout_columns, selectedTemplate)
    );

    useEffect(() => {
    const handleResize = () => {
        setSlidesToShow(getSlidesToShow(window.innerWidth, layout_columns, selectedTemplate));
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // run on mount

    return () => window.removeEventListener("resize", handleResize);
    }, [layout_columns, selectedTemplate]);

    // Compute slidesToScroll conditionally
    const slidesToScroll = selectedTemplate === 'Small_Widget_Temp' ? 2 : 3;

    // Slider Settings
    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: slidesToScroll,
        arrows: true,
        autoplay: false,
        responsive: [
            {
            breakpoint: 1024,
            settings: {
                slidesToShow: Math.min(slidesToShow, 3),
                slidesToScroll: 1,
            },
            },
            {
            breakpoint: 768,
            settings: {
                slidesToShow: Math.min(slidesToShow, 2),
                slidesToScroll: 1,
            },
            },
            {
            breakpoint: 480,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
            },
            },
        ],
    };

    // Fetching data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (source_search_name) {
                    const payload = { source_type: source_type, username_or_url: source_search_name };
                    result = await searchInstagramProfile(payload);
                } else {
                    result = await getDefaultList();
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, source_type, source_search_name,]);

    //Change Color
    function getContrastColor(bgColor) {
        // Remove # if present
        const color = bgColor.replace('#', '');

        // Convert hex to RGB
        const r = parseInt(color.substr(0, 2), 16);
        const g = parseInt(color.substr(2, 2), 16);
        const b = parseInt(color.substr(4, 2), 16);

        // Calculate brightness using luminance formula
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        // Return white for dark backgrounds, black for light
        return brightness > 128 ? '#000000' : '#ffffff';
    }

    //Handle dynamic styles based on layout
   const dynamicStyle =
    Number(layout_is_full_width) === 1
        ? { margin: '0 auto' }
        : layout_custom_width
        ? {
            maxWidth: `${layout_custom_width}px`,
            margin: '0 auto',
            }
        : {};

    //Handle rows and columns
    const [visibleRows, setVisibleRows] = useState(Number(layout_rows) || 1);
    const columns = Number(layout_columns) || 3;

    //Update visibleRows when layout_rows changes
    useEffect(() => {
        const newRows = Number(layout_rows) || 1;
        setVisibleRows(newRows);
    }, [layout_rows]);
    const totalVisibleCount = visibleRows * columns;
    //Handle Load More
    const handleListLayoutLoadMore = () => {
        setVisibleRows((prev) => prev + 1);
    };

    // Map template name to a specific class
    const templateClassMap = {
        Profile_Widget_Temp: 'profile-template-widget',
        Grid_Temp: 'grid-template-widge',
        Small_Widget_Temp: 'small-widget-template-widget',
        Hashtag_Showcase_Temp: 'hashtag-showcase-template-widget',
        Post_Slider_Temp: 'post-slider-template-widget',
        Dark_Profile_Widget_Temp: 'dark-profile-widget-template-widget',
    };
    const templateClass = templateClassMap[selectedTemplate] || '';

    return (
        <div className={`upgadeify instagram_feed_inner ${templateClass} ${is_website_frontend}`} style={dynamicStyle}>
            {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>

            {feed_title && (
                <div className="upgadeify_instagram_feed">
                    <div className="upgadeify_instagram_feed_title">{feed_title}</div>
                </div>
            )}

            {Number(header_is_show_header) === 1 && (
                <div style={{backgroundColor: header_background_color,}}>
                    <div className='upgadeify_instagram_header'>
                        <div>
                            <a className='upgadeify_instagram_header_profile'
                                href={apiData?.data?.user_detail?.user_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                >
                                {Number(header_profile_picture) === 1 && (
                                    <img src={apiData?.data?.user_detail?.profile_pic_url} alt={apiData?.data?.user_detail?.full_name} />
                                )}
                                <div className='upgadeify_instagram_header_profile_content'>
                                    {Number(header_full_name) === 1 && (
                                        <div className='upgadeify_instagram_header_profile_name' style={{ color: header_text_color,}}>{apiData?.data?.user_detail?.full_name}
                                        {Number(header_verified_badge) === 1 && (
                                    <div className="upgadeify_instagram_header_verified_badge" title="Verified">
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.79131 0.202079C5.90764 0.0894369 6.09236 0.0894369 6.20869 0.202079L7.15665 1.12C7.23117 1.19217 7.33769 1.22071 7.43831 1.19547L8.71823 0.874508C8.87529 0.835121 9.03527 0.927484 9.07969 1.0832L9.44168 2.35212C9.47014 2.45188 9.54812 2.52986 9.64788 2.55831L10.9168 2.92031C11.0725 2.96473 11.1649 3.12471 11.1255 3.28177L10.8045 4.56169C10.7793 4.66231 10.8078 4.76883 10.88 4.84335L11.7979 5.79131C11.9106 5.90764 11.9106 6.09236 11.7979 6.20869L10.88 7.15665C10.8078 7.23117 10.7793 7.33769 10.8045 7.43831L11.1255 8.71823C11.1649 8.87529 11.0725 9.03527 10.9168 9.07969L9.64788 9.44168C9.54812 9.47014 9.47014 9.54812 9.44168 9.64788L9.07969 10.9168C9.03527 11.0725 8.87529 11.1649 8.71823 11.1255L7.43831 10.8045C7.33769 10.7793 7.23117 10.8078 7.15665 10.88L6.20869 11.7979C6.09236 11.9106 5.90764 11.9106 5.79131 11.7979L4.84335 10.88C4.76883 10.8078 4.66231 10.7793 4.56169 10.8045L3.28177 11.1255C3.12471 11.1649 2.96473 11.0725 2.92031 10.9168L2.55831 9.64788C2.52986 9.54812 2.45188 9.47014 2.35212 9.44168L1.0832 9.07969C0.927484 9.03527 0.835121 8.87529 0.874508 8.71823L1.19547 7.43831C1.22071 7.33769 1.19217 7.23117 1.12 7.15665L0.202079 6.20869C0.0894369 6.09236 0.0894369 5.90764 0.202079 5.79131L1.12 4.84335C1.19217 4.76883 1.22071 4.66231 1.19547 4.56169L0.874508 3.28177C0.835121 3.12471 0.927484 2.96473 1.0832 2.92031L2.35212 2.55831C2.45188 2.52986 2.52986 2.45188 2.55831 2.35212L2.92031 1.0832C2.96473 0.927484 3.12471 0.835121 3.28177 0.874508L4.56169 1.19547C4.66231 1.22071 4.76883 1.19217 4.84335 1.12L5.79131 0.202079Z" fill="#0095F6"></path>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M7.76998 4.15662C7.98274 3.94779 8.32768 3.94779 8.54043 4.15662C8.73682 4.34938 8.75193 4.6527 8.58575 4.86247L8.54043 4.91284L5.55476 7.84338C5.35837 8.03614 5.04935 8.05097 4.83563 7.88786L4.78431 7.84338L3.35957 6.534C3.14681 6.32518 3.14681 5.9866 3.35957 5.77778C3.55596 5.58502 3.86498 5.57019 4.0787 5.73329L4.13002 5.77778L5.16953 6.70863L7.76998 4.15662Z" fill="white"></path>
                                        </svg>
                                    </div>
                                    )}
                                        </div>
                                    )}
                                
                                    {Number(header_username) === 1 && (
                                        <div className='upgadeify_instagram_header_profile_username' style={{ color: header_text_color,}}>@{apiData?.data?.user_detail?.username} </div>
                                    )}
                                </div>
                            </a>
                        </div>
                        <div className='upgadeify_instagram_header_profile_conter'>
                            {Number(header_posts_count) === 1 && (
                                <div className='upgadeify_instagram_header_conter_no' style={{ color: header_text_color,}}>{apiData?.data?.user_detail?.post_count} <span>Posts</span></div>
                            )}
                            {Number(header_followers_count) === 1 && (
                                <div className='upgadeify_instagram_header_conter_no' style={{ color: header_text_color,}}>{apiData?.data?.user_detail?.follower_count} <span>Followers</span></div>
                            )}
                            {Number(header_following_count) === 1 && (
                                <div className='upgadeify_instagram_header_conter_no' style={{ color: header_text_color,}}>{apiData?.data?.user_detail?.following_count} <span>Following</span></div>
                            )}
                        </div>
                        {Number(header_follow_button) === 1 && (
                            <a
                                href={apiData?.data?.user_detail?.user_url}
                                className="upgadeify_instagram_header_profile_action"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{backgroundColor: header_follow_button_color,color: header_follow_button_text_color,}}
                                >   
                                Follow
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/*If Grid Layout Type*/}
            {selectedLayout === 'grid_layout' && (
                <>
                    <div className='upgadeify-grid-layout' style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    }}>
                        {apiData.data.post_list.slice(0, totalVisibleCount).map((post) => (
                            <LoopItemData key={post.id} post={post} instagramFeedTabData={instagramFeedTabData} />
                        ))}
                        
                    </div>
                    {/* Load More Button  */}
                        {totalVisibleCount < apiData.data.post_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{
                                    backgroundColor: load_more_button,
                                    color: getContrastColor(load_more_button),
                                }}>Load More</button>
                            </div>
                        )}
                </>
            )}

            {/*If Slider Layout Type*/}
            {selectedLayout === 'slider_layout' && (
                <div className='upgadeify-slider-layout'>
                    <Slider {...sliderSettings}>
                        {apiData.data.post_list.map((post) => (
                            <LoopItemData key={post.id} post={post} instagramFeedTabData={instagramFeedTabData} />
                        ))}
                    </Slider>
                </div>
            )}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Post Data found please try.</p>
                </div>
            )}
        </div> 
    );
};

const LoopItemData = ({ post, instagramFeedTabData }) => {
    return (
        <div className='upgadeify_instagram_item' style={{ padding: instagramFeedTabData.layout_item_spacing,}}>
            <a href={post.url} className='upgadeify_instagram_link'>
                <div className='upgadeify_instagram_image'>
                    <img src={post.post_image} alt={post.post_short_code} />
                    <div className='upgadeify_instagram_icon'>
                        {post.post_type === 'Video' ? (
                            <svg viewBox="0 0 24 24">
                                <path d="M23.467,5.762c-0.118-0.045-0.232-0.068-0.342-0.068c-0.246,0-0.451,0.087-0.615,0.26l-3.76,3.217v5.766l3.76,3.578c0.164,0.173,0.369,0.26,0.615,0.26c0.109,0,0.223-0.023,0.342-0.068C23.822,18.552,24,18.284,24,17.901V6.57C24,6.186,23.822,5.917,23.467,5.762z"></path>
                                <path d="M16.33,4.412c-0.77-0.769-1.696-1.154-2.78-1.154H3.934c-1.084,0-2.01,0.385-2.78,1.154C0.385,5.182,0,6.108,0,7.192v9.616c0,1.084,0.385,2.01,1.154,2.78c0.77,0.77,1.696,1.154,2.78,1.154h9.616c1.084,0,2.01-0.385,2.78-1.154c0.77-0.77,1.154-1.696,1.154-2.78v-3.076v-3.478V7.192C17.484,6.108,17.099,5.182,16.33,4.412z M8.742,17.229c-2.888,0-5.229-2.341-5.229-5.229c0-2.888,2.341-5.229,5.229-5.229S13.971,9.112,13.971,12C13.971,14.888,11.63,17.229,8.742,17.229z"></path>
                            </svg>
                        ) : (
                            <svg viewBox="0 0 45.964 45.964">
                                <path d="M32.399,40.565H11.113v1.297c0,2.24,1.838,4.051,4.076,4.051h26.733c2.239,0,4.042-1.811,4.042-4.051V15.13c0-2.237-1.803-4.068-4.042-4.068h-1.415v21.395C40.507,36.904,36.845,40.566,32.399,40.565z"></path>
                                <path d="M0,4.102l0,28.355c0,2.241,1.814,4.067,4.051,4.067h28.365c2.237,0,4.066-1.826,4.066-4.067l0-28.356c0-2.238-1.828-4.051-4.066-4.051H4.051C1.814,0.05,0,1.862,0,4.102z"></path>
                            </svg>
                        )}
                    </div>
                </div>    
                {(
                    Number(instagramFeedTabData.post_likes_count) === 1 ||
                    Number(instagramFeedTabData.post_comments_count) === 1 ||
                    Number(instagramFeedTabData.post_text) === 1
                ) && (                   
                <div className='upgadeify_instagram_content' style={{ backgroundColor: instagramFeedTabData.header_post_overlay_background }}
                    >
                    <div className='upgadeify_instagram_content_header'>
                        {Number(instagramFeedTabData.post_likes_count) === 1 && (
                            <div className='upgadeify_instagram_like_share' style={{ color: instagramFeedTabData.header_post_overlay_text}}>
                               
                                <svg className="eapps-instagram-feed-posts-item-likes-count-icon" viewBox="0 0 24 24">
                                    <path d="M17.7,1.5c-2,0-3.3,0.5-4.9,2.1c0,0-0.4,0.4-0.7,0.7c-0.3-0.3-0.7-0.7-0.7-0.7c-1.6-1.6-3-2.1-5-2.1C2.6,1.5,0,4.6,0,8.3
                                    c0,4.2,3.4,7.1,8.6,11.5c0.9,0.8,1.9,1.6,2.9,2.5c0.1,0.1,0.3,0.2,0.5,0.2s0.3-0.1,0.5-0.2c1.1-1,2.1-1.8,3.1-2.7
                                    c4.8-4.1,8.5-7.1,8.5-11.4C24,4.6,21.4,1.5,17.7,1.5z M14.6,18.6c-0.8,0.7-1.7,1.5-2.6,2.3c-0.9-0.7-1.7-1.4-2.5-2.1
                                    c-5-4.2-8.1-6.9-8.1-10.5c0-3.1,2.1-5.5,4.9-5.5c1.5,0,2.6,0.3,3.8,1.5c1,1,1.2,1.2,1.2,1.2C11.6,5.9,11.7,6,12,6.1
                                    c0.3,0,0.5-0.2,0.7-0.4c0,0,0.2-0.2,1.2-1.3c1.3-1.3,2.1-1.5,3.8-1.5c2.8,0,4.9,2.4,4.9,5.5C22.6,11.9,19.4,14.6,14.6,18.6z" fill={instagramFeedTabData.header_post_overlay_text}></path>
                                </svg>
                                {post.post_likes}
                            </div>
                        )}
                        {Number(instagramFeedTabData.post_comments_count) === 1 && (
                            <div className='upgadeify_instagram_like_share' style={{ color: instagramFeedTabData.header_post_overlay_text}}>
                                
                                <svg className="eapps-instagram-feed-posts-item-comments-count-icon" viewBox="0 0 24 24">
                                    <path d="M1,11.9C1,17.9,5.8,23,12,23c1.9,0,3.7-1,5.3-1.8l5,1.3l0,0c0.1,0,0.1,0,0.2,0c0.4,0,0.6-0.3,0.6-0.6c0-0.1,0-0.1,0-0.2
                                    l-1.3-4.9c0.9-1.6,1.4-2.9,1.4-4.8C23,5.8,18,1,12,1C5.9,1,1,5.9,1,11.9z M2.4,11.9c0-5.2,4.3-9.5,9.5-9.5c5.3,0,9.6,4.2,9.6,9.5
                                    c0,1.7-0.5,3-1.3,4.4l0,0c-0.1,0.1-0.1,0.2-0.1,0.3c0,0.1,0,0.1,0,0.1l0,0l1.1,4.1l-4.1-1.1l0,0c-0.1,0-0.1,0-0.2,0
                                    c-0.1,0-0.2,0-0.3,0.1l0,0c-1.4,0.8-3.1,1.8-4.8,1.8C6.7,21.6,2.4,17.2,2.4,11.9z" fill={instagramFeedTabData.header_post_overlay_text}></path>
                                </svg>
                                {post.post_comments}
                            </div>
                        )}
                    </div>
                    {Number(instagramFeedTabData.post_text) === 1 && (
                        <div className='upgadeify_instagram_description' style={{ color: instagramFeedTabData.header_post_overlay_text}}>{post.title}</div>
                    )}
                </div>
                )}
            </a>    
        </div>
    );
};

export default CommonTemplateWidget;
